// src/models/egresoCabModel.js

import pb from '../lib/pocketbase';
import { buildFechaRangeFilter } from '../lib/guatemalaTime.js';

const FULL_LIST_BATCH_SIZE = 200;
const RELATION_FILTER_CHUNK_SIZE = 40;

function escapeFilterValue(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function buildRelationOrFilter(field, ids) {
  if (!ids.length) {
    return null;
  }

  return ids
    .map((id) => `${field} = "${escapeFilterValue(id)}"`)
    .join(' || ');
}

function buildSearchClause(searchQuery = '') {
  const trimmedSearch = searchQuery?.trim() || '';
  if (!trimmedSearch) return '';

  const query = escapeFilterValue(trimmedSearch);
  if (/^\d+$/.test(trimmedSearch)) {
    return `(nombre ~ "${query}" || numero ~ "${query}" || numero = ${trimmedSearch} || nit ~ "${query}" || uuid ~ "${query}")`;
  }
  return `(nombre ~ "${query}" || numero ~ "${query}" || nit ~ "${query}" || uuid ~ "${query}")`;
}

function buildDashboardFilter({
  startDate = '',
  endDate = '',
  deliveryMethod = '',
  sucursal = '',
  searchQuery = '',
} = {}) {
  const filters = [];
  const searchClause = buildSearchClause(searchQuery);

  // When searching, look across all dates (business date lives in `fecha`)
  if (searchClause) {
    filters.push(searchClause);
  } else {
    filters.push(...buildFechaRangeFilter(startDate, endDate));
  }

  if (deliveryMethod) {
    filters.push(`delivery_method = "${escapeFilterValue(deliveryMethod)}"`);
  }
  if (sucursal) {
    filters.push(`sucursal = "${escapeFilterValue(sucursal)}"`);
  }

  return filters.join(' && ');
}

async function getFullListByRelationIds(collection, field, ids, expand) {
  if (!ids.length) {
    return [];
  }

  const chunks = [];
  for (let i = 0; i < ids.length; i += RELATION_FILTER_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + RELATION_FILTER_CHUNK_SIZE));
  }

  const results = await Promise.all(
    chunks.map((chunk) => {
      const filter = buildRelationOrFilter(field, chunk);
      const options = {
        filter,
        batch: FULL_LIST_BATCH_SIZE,
        $autoCancel: false,
      };
      if (expand) options.expand = expand;
      return pb.collection(collection).getFullList(options);
    })
  );

  return results.flat();
}

function buildRelationMap(items) {
  const map = new Map();

  items.forEach((item) => {
    const key = item.egresoCab;
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key).push(item);
  });

  return map;
}

function enrichEgresoRecord(egreso, pickerMap, escoltaMap, corredorMap) {
  const pickers = pickerMap.get(egreso.id) || [];
  const escoltas = escoltaMap.get(egreso.id) || [];
  const corredores = corredorMap.get(egreso.id) || [];

  let pickerName = 'Sin mesa';
  if (pickers.length > 0 && pickers[0].expand?.picker) {
    pickerName = pickers[0].expand.picker.name || 'Sin nombre';
  }

  const escoltaNames = escoltas
    .map((item) => item.expand?.escolta?.nombre)
    .filter(Boolean);
  const escoltaName = escoltaNames.length > 0 ? escoltaNames.join(', ') : 'Sin escolta';

  let corredorName = 'Sin corredor';
  if (corredores.length > 0 && corredores[0].expand?.corredor) {
    corredorName = corredores[0].expand.corredor.nombre || 'Sin nombre';
  }

  let deliveryName = 'Sin asignar';
  if (egreso.expand?.delivery?.nombre) {
    deliveryName = egreso.expand.delivery.nombre;
  }

  if (egreso.delivery_method === 'Transporte') {
    deliveryName = deliveryName !== 'Sin asignar' ? deliveryName : 'Transporte asignado';
  } else if (egreso.delivery_method === 'Repartidor') {
    deliveryName = deliveryName !== 'Sin asignar' ? deliveryName : 'Sin asignar';
  } else if (egreso.delivery_method === 'Despacho') {
    deliveryName = deliveryName !== 'Sin asignar' ? deliveryName : 'Despacho directo';
  }

  return {
    ...egreso,
    picker_name: pickerName,
    escolta_name: escoltaName,
    escolta_names: escoltaNames,
    corredor_name: corredorName,
    delivery_name: deliveryName,
  };
}

// Obtener lista de egresos con paginación
export async function getEgresoCabs(page = 1, perPage = 10) {
  try {
    const response = await pb.collection('egresoCab').getList(page, perPage, {
      sort: '-created',
      $autoCancel: false,
    });
    return {
      items: response.items,
      totalItems: response.totalItems,
      page: response.page || response.currentPage || page,
    };
  } catch (error) {
    console.error('Error al obtener egresoCabs:', error);
    throw error;
  }
}

// Obtener lista de egresos con datos expandidos (picker, escolta, corredor)
export async function getEgresoCabsWithExpandedData(filters = {}) {
  try {
    const filter = buildDashboardFilter(filters);
    const listOptions = {
      sort: '-created',
      $autoCancel: false,
      expand: 'delivery',
      batch: FULL_LIST_BATCH_SIZE,
    };

    if (filter) {
      listOptions.filter = filter;
    }

    const response = await pb.collection('egresoCab').getFullList(listOptions);

    if (!response.length) {
      return {
        items: [],
        totalItems: 0,
        page: 1,
      };
    }

    const egresoIds = response.map((egreso) => egreso.id);

    const [allPickers, allEscoltas, allCorredores] = await Promise.all([
      getFullListByRelationIds('egresoPicker', 'egresoCab', egresoIds, 'picker'),
      getFullListByRelationIds('egresoEscolta', 'egresoCab', egresoIds, 'escolta'),
      getFullListByRelationIds('egresoCorredor', 'egresoCab', egresoIds, 'corredor'),
    ]);

    const pickerMap = buildRelationMap(allPickers);
    const escoltaMap = buildRelationMap(allEscoltas);
    const corredorMap = buildRelationMap(allCorredores);

    const processedResponse = response.map((egreso) =>
      enrichEgresoRecord(egreso, pickerMap, escoltaMap, corredorMap)
    );

    return {
      items: processedResponse,
      totalItems: processedResponse.length,
      page: 1,
    };
  } catch (error) {
    console.error('Error al obtener egresoCabs con datos expandidos:', error);
    throw error;
  }
}

// Crear un nuevo egreso
export async function createEgresoCab(data) {
  try {
    const response = await pb.collection('egresoCab').create(data);
    return response;
  } catch (error) {
    console.error('Error al crear egresoCab:', error);
    throw error;
  }
}

// Actualizar un egreso existente
export async function updateEgresoCab(id, data) {
  try {
    const response = await pb.collection('egresoCab').update(id, data);
    return response;
  } catch (error) {
    console.error('Error al actualizar egresoCab:', error);
    throw error;
  }
}

// Eliminar un egreso
export async function deleteEgresoCab(id) {
  try {
    const response = await pb.collection('egresoCab').delete(id);
    return response;
  } catch (error) {
    console.error('Error al eliminar egresoCab:', error);
    throw error;
  }
}

// Obtener un egreso por ID
export async function getEgresoCabById(id) {
  try {
    const response = await pb.collection('egresoCab').getOne(id);
    return response;
  } catch (error) {
    console.error('Error al obtener egresoCab por ID:', error);
    throw error;
  }
}


// ==================== ESCOLTA-RELATED METHODS ====================

// Obtener escoltas asociadas a un egreso
export async function getEgresoEscoltas(egresoCabId, page = 1, perPage = 10) {
  try {
    const response = await pb.collection('egresoEscolta').getList(page, perPage, {
      filter: `egresoCab = "${egresoCabId}"`,
      $autoCancel: false,
      expand: "escolta",
    });
    return {
      items: response.items,
      totalItems: response.totalItems,
      page: response.currentPage,
    };
  } catch (error) {
    console.error("Error fetching egresoEscolta:", error);
    throw new Error("Error al obtener los detalles de egreso");
  }
}

// Agregar escoltas a un egreso
export async function addEscoltasToEgreso(egresoId, escoltaIds) {
  try {
    const promises = escoltaIds.map(escoltaId =>
      pb.collection('egresoEscolta').create(
        {
          egresoCab: egresoId,
          escolta: escoltaId,
        },
        { $autoCancel: false }
      )
    );
    await Promise.all(promises);
    return { success: true };
  } catch (error) {
    console.error('Error al agregar escoltas a egreso:', error);
    throw error;
  }
}

// Eliminar escoltas de un egreso
export async function deleteEscoltasFromEgreso(egresoId) {
  try {
    const existingEscoltas = await pb.collection('egresoEscolta').getFullList({
      filter: `egresoCab = "${egresoId}"`, // Filter by egresoCab ID
      $autoCancel: false,
    });
    for (const escolta of existingEscoltas) {
      await pb.collection('egresoEscolta').delete(escolta.id); // Delete each escolta
    }
    return { success: true };
  } catch (error) {
    console.error('Error al eliminar escoltas de egreso:', error);
    throw error;
  }
}


// Obtener escoltas asociadas a un egreso
export async function getEgresoCorredores(egresoCabId, page = 1, perPage = 10) {
  try {
    const response = await pb.collection('egresoCorredor').getList(page, perPage, {
      filter: `egresoCab = "${egresoCabId}"`,
      $autoCancel: false,
      expand: "corredor",
    });
    return {
      items: response.items,
      totalItems: response.totalItems,
      page: response.currentPage,
    };
  } catch (error) {
    console.error("Error fetching egresoEscolta:", error);
    throw new Error("Error al obtener los detalles de egreso");
  }
}

// Obtener pickers asociados a un egreso
export async function getEgresoPickers(egresoCabId, page = 1, perPage = 10) {
  try {
    const response = await pb.collection('egresoPicker').getList(page, perPage, {
      filter: `egresoCab = "${egresoCabId}"`,
      $autoCancel: false,
      expand: "picker",
    });
    return {
      items: response.items,
      totalItems: response.totalItems,
      page: response.currentPage,
    };
  } catch (error) {
    console.error("Error fetching egresoPicker:", error);
    throw new Error("Error al obtener los detalles de egreso");
  }
}

// ==================== PICKER-RELATED METHODS ====================

export async function deletePickersFromEgreso(egresoId) {
  try {
    const existing = await pb.collection('egresoPicker').getFullList({
      filter: `egresoCab = "${egresoId}"`,
      $autoCancel: false,
    });
    for (const row of existing) {
      await pb.collection('egresoPicker').delete(row.id);
    }
    return { success: true };
  } catch (error) {
    console.error('Error al eliminar pickers de egreso:', error);
    throw error;
  }
}

export async function setEgresoPicker(egresoId, pickerId) {
  try {
    await deletePickersFromEgreso(egresoId);
    if (pickerId) {
      await pb.collection('egresoPicker').create(
        {
          egresoCab: egresoId,
          picker: pickerId,
        },
        { $autoCancel: false }
      );
    }
    return { success: true };
  } catch (error) {
    console.error('Error al asignar picker a egreso:', error);
    throw error;
  }
}

async function enrichEgresosWithPickers(egresos) {
  const egresoIds = egresos.map((egreso) => egreso.id);
  const allPickers = await getFullListByRelationIds(
    'egresoPicker',
    'egresoCab',
    egresoIds,
    'picker'
  );
  const pickerMap = buildRelationMap(allPickers);
  return egresos.map((egreso) =>
    enrichEgresoRecord(egreso, pickerMap, new Map(), new Map())
  );
}

/** Pedidos en ASIGNACION_MESA asignados a este picker (legado / panel). */
export async function getEgresoCabsForPicker(userId) {
  try {
    const pickerLinks = await pb.collection('egresoPicker').getFullList({
      filter: `picker = "${escapeFilterValue(userId)}"`,
      $autoCancel: false,
    });

    const cabIds = [...new Set(pickerLinks.map((link) => link.egresoCab))];
    if (!cabIds.length) {
      return { items: [], totalItems: 0, page: 1 };
    }

    const idFilter = buildRelationOrFilter('id', cabIds);
    const filter = `(${idFilter}) && sta = "ASIGNACION_MESA"`;

    const response = await pb.collection('egresoCab').getFullList({
      filter,
      sort: '-created',
      expand: 'delivery',
      $autoCancel: false,
    });

    const processedResponse = await enrichEgresosWithPickers(response);

    return {
      items: processedResponse,
      totalItems: processedResponse.length,
      page: 1,
    };
  } catch (error) {
    console.error('Error al obtener egresos del picker:', error);
    throw error;
  }
}

/**
 * Tablero mesa: autorizados (pool libre) + mis pedidos en asignación de mesa.
 */
export async function getEgresoCabsForPickerBoard(userId, { searchQuery = '' } = {}) {
  try {
    const searchClause = buildSearchClause(searchQuery);
    const autorizadoFilter = searchClause
      ? `sta = "AUTORIZADO_POR_CREDITOS" && ${searchClause}`
      : 'sta = "AUTORIZADO_POR_CREDITOS"';

    const autorizadoRaw = await pb.collection('egresoCab').getFullList({
      filter: autorizadoFilter,
      sort: '-created',
      expand: 'delivery',
      $autoCancel: false,
    });

    // Ya tomados por cualquier mesa: fuera del pool Autorizado
    const autorizadoIds = autorizadoRaw.map((row) => row.id);
    const takenLinks = await getFullListByRelationIds(
      'egresoPicker',
      'egresoCab',
      autorizadoIds,
      ''
    );
    const takenIds = new Set(takenLinks.map((link) => link.egresoCab).filter(Boolean));
    const autorizadoFree = autorizadoRaw.filter((row) => !takenIds.has(row.id));

    const pickerLinks = await pb.collection('egresoPicker').getFullList({
      filter: `picker = "${escapeFilterValue(userId)}"`,
      $autoCancel: false,
    });
    const myCabIds = [...new Set(pickerLinks.map((link) => link.egresoCab))];

    let asignacionRaw = [];
    if (myCabIds.length) {
      const idFilter = buildRelationOrFilter('id', myCabIds);
      const asignacionFilter = searchClause
        ? `(${idFilter}) && sta = "ASIGNACION_MESA" && ${searchClause}`
        : `(${idFilter}) && sta = "ASIGNACION_MESA"`;
      asignacionRaw = await pb.collection('egresoCab').getFullList({
        filter: asignacionFilter,
        sort: '-created',
        expand: 'delivery',
        $autoCancel: false,
      });
    }

    const [autorizado, asignacion] = await Promise.all([
      enrichEgresosWithPickers(autorizadoFree),
      enrichEgresosWithPickers(asignacionRaw),
    ]);

    return { autorizado, asignacion };
  } catch (error) {
    console.error('Error al obtener tablero del picker:', error);
    throw error;
  }
}

export async function claimEgresoForPicker(egresoId, userId) {
  if (!egresoId || !userId) {
    throw new Error('egresoId y userId son requeridos');
  }

  // Prefer server hook (bypasses collection rules + guarantees both writes)
  try {
    const res = await pb.send('/api/picking/claim', {
      method: 'POST',
      body: { egresoId },
    });
    if (res?.success) {
      return { success: true, via: 'api' };
    }
  } catch (apiErr) {
    const msg = String(apiErr?.message || apiErr?.data?.message || '');
    if (/ya tomado|otra mesa|already/i.test(msg)) {
      const err = new Error('Pedido ya tomado por otra mesa');
      err.code = 'ALREADY_CLAIMED';
      throw err;
    }
    // Fall through to client-side claim if hook not deployed yet
    console.warn('Claim API unavailable, falling back to client write:', apiErr);
  }

  const existing = await pb.collection('egresoPicker').getFullList({
    filter: `egresoCab = "${escapeFilterValue(egresoId)}"`,
    $autoCancel: false,
  });

  const takenByOther = existing.find((row) => row.picker && row.picker !== userId);
  if (takenByOther) {
    const err = new Error('Pedido ya tomado por otra mesa');
    err.code = 'ALREADY_CLAIMED';
    throw err;
  }

  // Status first so Autorizado pool clears even if picker link lags
  await updateEgresoCab(egresoId, { sta: 'ASIGNACION_MESA' });
  await setEgresoPicker(egresoId, userId);

  return { success: true, via: 'client' };
}

